/**
 * 代理商门户 API 边界（live）
 * 正式前缀：/agent/api/v1
 *
 * FE-E2b 薄壳化：公共核心下沉 @shared/api/createClient 工厂；本文件保留
 * agent 端配置、类型与端特有分支（client 内 formatApiError / blob 字节流下载），
 * 导出面与行为不变（FE-E2a 契约测试 client.contract.test.ts 锁定）。
 *
 * 会话闭环（镜像 customer US-002 契约）：
 * - session 键 allinks_agent_session；作用域 id = agentId（来自 me.agentId）
 * - 鉴权请求注入 Authorization: Bearer <accessToken>
 * - 401 + code ∈ {AUTH_401, AUTH_401_X} → refresh（去重）→ 重放最多 1 次
 * - 非过期 401 / refresh 失败 → signOutLocal（清会话 + 跳登录）
 */
import { createClient } from '@shared/api/createClient';
import { mapApiError } from '../lib/apiErrorMessages';

/** 演示/联调默认 agentId；Long 已由后端统一序列化为 JSON string（Sprint §2.3 契约），保持字符串透传 */
export const DEFAULT_AGENT_ID = '10';

/**
 * 统一失败信息（US-201 模式基础）：
 * - code：后端信封业务码（如 EMAIL_NOT_VERIFIED / 3104）；网络异常等非信封失败缺省
 * - status：HTTP 状态（0 = fetch 层网络异常）；供「业务码 vs 网络异常」分流展示
 * - traceId：后端信封追踪码（系统级错误时 error 已附加，字段供页面自行展示）
 */
export type ApiError = {
  error: string;
  code?: string;
  status?: number;
  traceId?: string;
};

export type ApiResult<T> = { ok: true; data: T } | ({ ok: false } & ApiError);

/**
 * 业务码可见回显统一文案（US-201）：委托 lib/apiErrorMessages 的 mapApiError
 * （单一真源：业务码 → 可行动中文映射；未收录码 message 在前、业务码括注在后，
 * 满足合同 §3.4「文案含业务码或其映射文案」；网络异常/超时按传输层形态分流）。
 */
export function formatApiError(e: ApiError): string {
  return mapApiError(e.code, e.error);
}

// ================= 会话与领域视图类型（对齐后端 DTO） =================

/** 认证渠道（对齐后端 AuthChannel 枚举字面量） */
export type AuthChannel = 'ADMIN' | 'AGENT' | 'MERCHANT' | 'CARDHOLDER';

/** 当前会话身份视图（对齐后端 PortalSessionView record；Long 字段已按 US-102 契约字符串化） */
export type PortalSessionView = {
  subjectId?: string;
  channel?: AuthChannel;
  principal?: string;
  roles?: string[];
  permissions?: string[];
  merchantId?: string;
  agentId?: string;
  cardholderId?: string;
  emailVerified?: boolean;
  emailMasked?: string;
  mustChangePassword?: boolean;
  mfaSetupRequired?: boolean;
  /** OTP 宽限剩余秒；无宽限为 0（对齐后端 PortalSessionView） */
  otpGraceRemainingSeconds?: number;
  /** 代登录会话标识（契约 §0.4：me 响应末位字段，R75） */
  impersonated?: boolean;
};

/** 运行时菜单节点（对齐后端 MenuNodeView record） */
export type MenuNodeView = {
  id: string;
  title: string;
  path?: string | null;
  permission?: string | null;
  children?: MenuNodeView[];
};

/** 代理商会话；作用域 id = agentId（登录后由 /auth/me 写入；US-102 起为字符串形态 ID） */
export type AgentSession = {
  accessToken?: string;
  refreshToken?: string;
  principal?: string;
  /** /auth/me 结果（登录后写入；拉取失败置 null） */
  me?: PortalSessionView | null;
  /** /auth/menus 结果（登录后写入；拉取失败置 null） */
  menus?: MenuNodeView[] | null;
  /** accessToken 预估过期时间戳（ms）；缺省表示未知 */
  expiresAt?: number;
  /** 作用域代理商 id（来自 me.agentId；登录前可能缺省；字符串透传，禁 Number 化） */
  agentId?: string;
  /** 运营代登录会话标识（/auth/portal-login 换取；顶层持久化，响应无 refreshToken 不可续期 D3） */
  impersonated?: boolean;
};

/** live-only（2026-08-20 LIVE 切换延续）：mock 回调保留但恒关，紧急回滚改此行重新构建 */
export const USE_MOCK = false;

/** 会话写入后广播，供 RequireSetup / Layout 重读 me 标志 */
export const SESSION_CHANGED_EVENT = 'allinks-session';

// ================= 工厂实例（端差异参数化） =================

const core = createClient<AgentSession>({
  apiBase: {
    dev: '/agent/api/v1',
    prod: 'https://allinks-api.swapsle.com/agent/api/v1',
  },
  sessionKey: 'allinks_agent_session',
  loginPath: '/login',
  // agent 现状锁定（FE-E2a 疑虑 Y2）：opts.headers 仅类型声明，rawRequest 不合并
  // 审查 C3 修复：'ignore' 会让 opts.headers（X-OTP）静默丢弃——security.ts 的踢设备/授信/
  // 取消授信在真实模式恒 3410。改 'spread' 直并自定义头；影响面已核：全 agent api 仅
  // security.ts 三处传 headers，无其他调用依赖 'ignore' 行为（X-OTP 均为合法自定义头）。
  customHeaderMode: 'spread',
  // agent 幂等键回退形态：idem-{ts}-{random36}（带随机后缀，保持原行为）
  jsonKeyGen: 'ts-random36',
  uploadKeyGen: 'ts-random36',
  // US-201：失败信封保留 status 字段（业务码 vs 网络异常分流）
  includeStatus: true,
});

export const AGENT_API_BASE = core.apiBase;

export function agentApiUrl(path: string): string {
  return core.apiUrl(path);
}

export function loadSession(): AgentSession | null {
  return core.loadSession();
}

export function saveSession(s: AgentSession) {
  core.saveSession(s);
}

export function clearSession() {
  core.clearSession();
}

export function setAuthFailureHandler(fn: (() => void) | null) {
  core.setAuthFailureHandler(fn);
}

/** 清会话 + 跳登录：refresh 失败 / 非过期 401 / 主动登出均走此路径 */
export function signOutLocal() {
  core.signOutLocal();
}

// ================= 公共 API（保持既有签名，向后兼容） =================

export async function agentGet<T>(path: string, mock: () => T): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('GET', path, undefined);
}

export async function agentPost<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string>; idempotencyKey?: string },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('POST', path, body, opts);
}

/** 写操作（更新）；PUT 带 JSON body + Idempotency-Key；走 fetchWithRefresh */
export async function agentPut<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('PUT', path, body, opts);
}

/** 删除 / 停用；无 body；走 fetchWithRefresh */
export async function agentDelete<T>(
  path: string,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('DELETE', path, undefined, opts);
}

/** multipart 文件上传；与 JSON client 使用同一会话及 401 refresh 契约。 */
export async function agentUpload<T>(
  path: string,
  formData: FormData,
  mock: () => T,
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.upload<T>(path, formData);
}

// ================= 鉴权 Blob（证书/协议 PDF；不改登录/会话语义） =================

type BlobResult =
  | { ok: true; blob: Blob; fileName?: string }
  | { ok: false; error: string; code?: string; status?: number; traceId?: string };

const AGENT_PORTAL_PREFIX = '/agent/api/v1';

/**
 * 将契约中的同源相对路径解析为可 fetch 的 URL。
 * 后端 downloadUrl / fileUrl 可能是 `/profile/...` 或 `/agent/api/v1/profile/...`。
 */
function resolveAgentResourceUrl(pathOrUrl: string): string {
  const raw = pathOrUrl.trim();
  if (!raw) {
    return agentApiUrl('/');
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  if (withSlash === AGENT_API_BASE || withSlash.startsWith(`${AGENT_API_BASE}/`)) {
    return withSlash;
  }
  if (withSlash === AGENT_PORTAL_PREFIX || withSlash.startsWith(`${AGENT_PORTAL_PREFIX}/`)) {
    return agentApiUrl(withSlash.slice(AGENT_PORTAL_PREFIX.length) || '/');
  }
  return agentApiUrl(withSlash);
}

function parseContentDispositionFileName(header: string | null): string | undefined {
  if (!header) {
    return undefined;
  }
  const utf8 = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    const value = utf8[1].trim().replace(/^["']|["']$/g, '');
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  const plain = /filename=([^;]+)/i.exec(header);
  if (plain?.[1]) {
    return plain[1].trim().replace(/^["']|["']$/g, '');
  }
  return undefined;
}

/**
 * GET 鉴权字节流（Bearer + 与 JSON 相同的 401 refresh）。
 * 证书/协议 PDF 必须走此路径：sessionStorage 的 Bearer 不会随 window.open 发出。
 * mock 回调须返回合法 PDF 字节。不改登录/会话语义。
 */
export async function agentGetBlob(
  path: string,
  mock: () => Blob | { blob: Blob; fileName?: string },
): Promise<BlobResult> {
  if (USE_MOCK) {
    await Promise.resolve();
    const payload = mock();
    if (payload instanceof Blob) {
      return { ok: true, blob: payload };
    }
    return { ok: true, blob: payload.blob, fileName: payload.fileName };
  }
  const { buildHeaders, appendTraceHint, timeoutSignal, retryOnceOn401, TRANSFER_TIMEOUT_MS } =
    core.internals;
  type RawBlob = { ok: true; data: { blob: Blob; fileName?: string } } | { ok: false; status: number; code?: string; error: string; traceId?: string };
  const rawBlobRequest = async (): Promise<RawBlob> => {
    try {
      const headers = buildHeaders(false);
      headers.Accept = 'application/pdf,application/octet-stream,*/*';
      const res = await fetch(resolveAgentResourceUrl(path), {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: timeoutSignal(TRANSFER_TIMEOUT_MS),
      });
      if (res.ok) {
        const bytes = await res.arrayBuffer();
        const contentType = (res.headers.get('Content-Type') || 'application/pdf').split(';')[0].trim();
        return {
          ok: true,
          data: {
            blob: new Blob([bytes], { type: contentType || 'application/pdf' }),
            fileName: parseContentDispositionFileName(res.headers.get('Content-Disposition')),
          },
        };
      }
      let error = `HTTP ${res.status}`;
      let code: string | undefined;
      let traceId: string | undefined;
      try {
        const body = (await res.json()) as { message?: string; code?: string | number; traceId?: string };
        if (body?.message) {
          error = body.message;
        } else if (body?.code !== undefined) {
          error = String(body.code);
        }
        if (body?.code !== undefined) {
          code = String(body.code);
        }
        traceId = body?.traceId;
      } catch {
        // 内容流错误体可能不是 JSON，保留 HTTP 状态
      }
      return { ok: false, status: res.status, code, error: appendTraceHint(code, error, traceId), traceId };
    } catch (e) {
      return { ok: false, status: 0, error: e instanceof Error ? e.message : 'network_error' };
    }
  };
  const res = await retryOnceOn401(rawBlobRequest);
  return res.ok
    ? { ok: true, blob: res.data.blob, fileName: res.data.fileName }
    : { ok: false, error: res.error, code: res.code, status: res.status, traceId: res.traceId };
}
